import { ClientSelector } from '@/components/client/ClientSelector'
import { HospitalSearchSelector } from '@/components/HospitalSearchSelector'
import { PartnerSelector } from '@/components/partner/PartnerSelector'
import { ProfessionalSearchSelector } from '@/components/ProfessionalSearchSelector'
import { useEntityAccess } from '@/lib/hooks/use-entity-access'
import { ReportFilters } from '@/lib/api/reports/reports-api'
import { useState } from 'react'
import { MdClear, MdExpandLess, MdExpandMore, MdFilterList } from 'react-icons/md'
import {
    BILLING_PAYMENT_METHOD_LABELS,
    BILLING_STATUS_LABELS,
    BILLING_STATUS_NONE,
    BILLING_STATUS_NONE_LABEL,
    PRODUCTIVITY_SALE_STATUSES,
    PRODUCTIVITY_STATUS_OPTIONS,
} from '../config/report-configs'
import MultiSelectFilter from '@/components/multi-select-filter'

interface ProductivityFiltersProps {
    filters: ReportFilters
    onChange: (filters: ReportFilters) => void
    /** Escopo dos dados (seletor "Empresa / Todas filiais" do topo da página). */
    dataScope?: { scope?: 'company' | 'organization'; companyId?: number }
}

const BILLING_STATUS_FILTER_OPTIONS = [
    ...Object.entries(BILLING_STATUS_LABELS).map(([value, label]) => ({
        value,
        label,
    })),
    { value: BILLING_STATUS_NONE, label: BILLING_STATUS_NONE_LABEL },
]

const BILLING_PAYMENT_METHOD_OPTIONS = Object.entries(
    BILLING_PAYMENT_METHOD_LABELS
).map(([value, label]) => ({ value, label }))

/**
 * Filtros da aba de Produtividade — **um bloco só, valendo para todos os tipos**.
 *
 * Antes cada tipo selecionado trazia a sua própria caixa de "Mais filtros", visualmente
 * idêntica às outras. O operador preenchia a primeira e as demais seguiam no padrão:
 * o recorte valia para consultas e os exames vinham inteiros, cancelados inclusive — e
 * o card de totais somava bases com critérios diferentes, sem avisar. Comissão apurada
 * assim sai errada e ninguém percebe.
 *
 * Aqui ficam apenas os filtros que os três tipos entendem. O que é exclusivo de um
 * ("é retorno?" em consultas, item do catálogo, material) continua na seção do
 * tipo, onde faz sentido.
 */
export default function ProductivityFilters({
    filters,
    onChange,
    dataScope,
}: ProductivityFiltersProps) {
    const { canSeeProfessionals } = useEntityAccess()
    const [expanded, setExpanded] = useState(false)

    const selectorScope =
        dataScope?.scope === 'organization' ? 'organization' : 'company'

    const activeCount = [
        filters.status,
        filters.billingStatus,
        filters.billingPaymentMethod,
        filters.professionalId,
        filters.clientId,
        filters.partnerId,
        filters.hospitalId,
    ].filter(value =>
        Array.isArray(value)
            ? value.length > 0
            : value !== undefined && value !== null && value !== ''
    ).length

    const clear = () => {
        // Período e base do período não são "filtros": eles definem o intervalo que
        // está na tela, e limpar não pode mudar o significado do que se está vendo.
        onChange({
            startDate: filters.startDate,
            endDate: filters.endDate,
            dateBasis: filters.dateBasis,
            // Limpar volta ao recorte de venda (sem orçamento e cancelado), não a "todos".
            status: PRODUCTIVITY_SALE_STATUSES,
        })
    }

    return (
        <div className="bg-base-100 rounded-xl border border-base-300/50">
            <button
                type="button"
                className="w-full flex items-center justify-between p-4 hover:bg-base-200/50 transition-colors"
                onClick={() => setExpanded(!expanded)}
            >
                <div className="flex items-center gap-2">
                    <MdFilterList className="text-xl text-secondary" />
                    <span className="font-semibold text-base-content">
                        Mais filtros
                    </span>
                    {activeCount > 0 && (
                        <span className="badge badge-secondary badge-sm">
                            {activeCount}
                        </span>
                    )}
                    <span className="text-xs text-base-content/50">
                        valem para todos os tipos selecionados
                    </span>
                </div>
                {expanded ? (
                    <MdExpandLess className="text-xl text-base-content/60" />
                ) : (
                    <MdExpandMore className="text-xl text-base-content/60" />
                )}
            </button>

            {expanded && (
                <div className="px-4 pb-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <MultiSelectFilter
                            label="Situação do atendimento"
                            options={PRODUCTIVITY_STATUS_OPTIONS.map(option => ({
                                value: option.value,
                                label: option.label,
                            }))}
                            value={filters.status}
                            onChange={values =>
                                onChange({ ...filters, status: values })
                            }
                        />

                        <MultiSelectFilter
                            label="Status da Cobrança"
                            options={BILLING_STATUS_FILTER_OPTIONS}
                            value={filters.billingStatus}
                            onChange={values =>
                                onChange({ ...filters, billingStatus: values })
                            }
                        />

                        <MultiSelectFilter
                            label="Meio de Pagamento"
                            options={BILLING_PAYMENT_METHOD_OPTIONS}
                            value={filters.billingPaymentMethod}
                            onChange={values =>
                                onChange({
                                    ...filters,
                                    billingPaymentMethod: values,
                                })
                            }
                        />

                        {canSeeProfessionals && (
                            <ProfessionalSearchSelector
                                value={filters.professionalId || null}
                                onChange={id =>
                                    onChange({
                                        ...filters,
                                        professionalId: id || undefined,
                                    })
                                }
                                label="Profissional"
                                placeholder="Buscar profissional..."
                            />
                        )}

                        <ClientSelector
                            value={filters.clientId || null}
                            onChange={id =>
                                onChange({ ...filters, clientId: id || undefined })
                            }
                            label="Cliente"
                            placeholder="Buscar cliente..."
                        />

                        <PartnerSelector
                            value={filters.partnerId || null}
                            onChange={id =>
                                onChange({ ...filters, partnerId: id || undefined })
                            }
                            label="Parceiro"
                            scope={selectorScope}
                            companyId={dataScope?.companyId}
                        />

                        <div className="form-control w-full">
                            <HospitalSearchSelector
                                value={filters.hospitalId || null}
                                onChange={id =>
                                    onChange({
                                        ...filters,
                                        hospitalId: id || undefined,
                                    })
                                }
                                label="Hospital"
                                placeholder="Buscar hospital..."
                            />
                        </div>
                    </div>

                    <div className="mt-3 flex items-start justify-between gap-3 flex-wrap">
                        {/* O par status-do-atendimento x status-da-cobrança confunde na
                            hora de apurar comissão: filtrar "Pago" traz o cancelado cuja
                            cobrança foi paga, porque são dimensões diferentes. */}
                        <p className="text-xs text-base-content/50 max-w-xl">
                            "Situação do atendimento" e "Status da Cobrança" são coisas
                            diferentes: uma consulta cancelada pode ter cobrança paga.
                            Para excluir os cancelados da comissão, marque as situações
                            que contam.
                        </p>
                        <button
                            type="button"
                            className="btn btn-sm btn-ghost gap-2"
                            onClick={clear}
                        >
                            <MdClear className="text-lg" />
                            Limpar Filtros
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}
